import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getCollection,
  getDocument,
  api
} from "../config";
import { v4 as uuidv4 } from "uuid";

export const loadMessageTemplates = createAsyncThunk(
  "messageTemplates/loadMessageTemplates",
  async (_, { getState, rejectWithValue }) => {
    const { account } = getState();
    const { user } = account;

    try {
      if (user.parentUserID) {
        // Invited user: load own templates AND parent's templates (read-only inherited)
        // Also fetch parent user document to get their name for the "Created by" label
        const [own, inherited, parentUserDoc] = await Promise.all([
          getCollection("message_templates", {
            where: [["userID", "==", user.id]],
          }),
          getCollection("message_templates", {
            where: [["userID", "==", user.parentUserID]],
          }),
          getDocument("users", user.parentUserID),
        ]);
        return {
          templates: [
            ...(own || []).filter((t) => !t.deleted).map((t) => ({ ...t, isInherited: false })),
            ...(inherited || []).filter((t) => !t.deleted).map((t) => ({ ...t, isInherited: true })),
          ],
          parentUserName: parentUserDoc?.name || null,
        };
      } else {
        // Parent user: load only own templates
        const rs = await getCollection("message_templates", {
          where: [["userID", "==", user.id]],
        });
        return {
          templates: (rs || []).filter((t) => !t.deleted),
          parentUserName: null,
        };
      }
    } catch(e) {
      return rejectWithValue(e.message);
    }
  }
);

export const saveMessageTemplate = createAsyncThunk(
  "messageTemplates/saveMessageTemplate",
  async (data, { getState, rejectWithValue }) => {
    const { account } = getState();
    const { user } = account;

    try {
      const payload = {
        id: data.id || uuidv4(),
        userID: user.id,
        type: data.type,
        title: data.title,
        body: data.body,
      };
      const response = await api.messageTemplateSave(payload);
      return { id: response.data.id, ...response.data.data };
    } catch(e) {
      return rejectWithValue(e.message);
    }
  }
);

export const deleteMessageTemplate = createAsyncThunk(
  "messageTemplates/deleteMessageTemplate",
  async (id, { rejectWithValue }) => {
    try {
      await api.messageTemplateDelete({ id });
      return id;
    } catch(e) {
      return rejectWithValue(e.message);
    }
  }
);

export const messageTemplatesSlice = createSlice({
  name: "messageTemplates",
  initialState: {
    templates: [],
    parentUserName: null,
    loading: false,
    loadingError: null,
    saving: false,
    savingError: null,
  },
  reducers: {},
  extraReducers: {
    [loadMessageTemplates.fulfilled]: (state, action) => {
      const payload = action.payload || {};
      state.templates = (payload.templates || []).filter(t => !t.deleted);
      state.parentUserName = payload.parentUserName || null;
      state.loading = false;
      return;
    },
    [loadMessageTemplates.pending]: (state) => {
      state.loading = true;
      state.loadingError = null;
      return;
    },
    [loadMessageTemplates.rejected]: (state, action) => {
      state.loadingError = action.payload;
      state.loading = false;
      return;
    },
    [saveMessageTemplate.fulfilled]: (state, action) => {
      const idx = state.templates.findIndex(t => t.id === action.payload.id);
      if (idx >= 0) {
        state.templates[idx] = action.payload;
      } else {
        state.templates.unshift(action.payload);
      }
      state.saving = false;
      state.savingError = null;
      return;
    },
    [saveMessageTemplate.pending]: (state) => {
      state.saving = true;
      state.savingError = null;
      return;
    },
    [saveMessageTemplate.rejected]: (state, action) => {
      state.savingError = action.payload;
      state.saving = false;
      return;
    },
    [deleteMessageTemplate.fulfilled]: (state, action) => {
      state.templates = state.templates.filter(t => t.id !== action.payload);
      return;
    },
  },
});

export const {} = messageTemplatesSlice.actions;
export default messageTemplatesSlice.reducer;
